import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CountdownToLaunch } from '../components/CountdownToLaunch';
import { Check, Rocket, Sparkles, Target, ArrowRight, Calendar, Trophy } from 'lucide-react';

const PHASES = [
    {
        key: 'q1',
        status: 'done',
        labelKey: 'roadmap.q1Label',
        titleKey: 'roadmap.q1Title',
        items: ['roadmap.q1Item1', 'roadmap.q1Item2', 'roadmap.q1Item3', 'roadmap.q1Item4', 'roadmap.q1Item5'],
        color: '#00FF66',
    },
    {
        key: 'q2',
        status: 'in_progress',
        labelKey: 'roadmap.q2Label',
        titleKey: 'roadmap.q2Title',
        items: ['roadmap.q2Item1', 'roadmap.q2Item2', 'roadmap.q2Item3', 'roadmap.q2Item4', 'roadmap.q2Item5'],
        color: '#FFD700',
        highlight: true,
    },
    {
        key: 'q3',
        status: 'planned',
        labelKey: 'roadmap.q3Label',
        titleKey: 'roadmap.q3Title',
        items: ['roadmap.q3Item1', 'roadmap.q3Item2', 'roadmap.q3Item3', 'roadmap.q3Item4'],
        color: '#9B6FE8',
    },
    {
        key: 'q4',
        status: 'planned',
        labelKey: 'roadmap.q4Label',
        titleKey: 'roadmap.q4Title',
        items: ['roadmap.q4Item1', 'roadmap.q4Item2', 'roadmap.q4Item3', 'roadmap.q4Item4'],
        color: '#FF6B35',
    },
];

function StatusBadge({ status }) {
    const { t } = useTranslation();
    if (status === 'done') {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#00FF66]/15 border border-[#00FF66]/30 text-[#00FF66] text-[10px] font-bold uppercase tracking-wider">
                <Check className="w-3 h-3" />
                {t('roadmap.statusDone')}
            </span>
        );
    }
    if (status === 'in_progress') {
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/30 text-[#FFD700] text-[10px] font-bold uppercase tracking-wider">
                <Rocket className="w-3 h-3" />
                {t('roadmap.statusInProgress')}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/15 text-white/60 text-[10px] font-bold uppercase tracking-wider">
            <Calendar className="w-3 h-3" />
            {t('roadmap.statusPlanned')}
        </span>
    );
}

export default function RoadmapPage() {
    const { t } = useTranslation();
    return (
        <div className="min-h-screen" data-testid="roadmap-page">
            {/* Hero */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div className="absolute inset-0 bg-gradient-to-br from-[#9B6FE8]/10 via-transparent to-[#FFD700]/10" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-20 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#9B6FE8]/15 border border-[#9B6FE8]/30 mb-6">
                        <Target className="w-3.5 h-3.5 text-[#9B6FE8]" />
                        <span className="text-[#9B6FE8] text-xs font-bold uppercase tracking-widest">
                            {t('roadmap.heroBadge')}
                        </span>
                    </div>
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-4">
                        {t('roadmap.heroTitle1')} <span className="text-[#FFD700]">{t('roadmap.heroTitle2')}</span>
                    </h1>
                    <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
                        {t('roadmap.heroDesc')}
                    </p>
                    <CountdownToLaunch />
                </div>
            </section>

            {/* Timeline */}
            <section className="py-20 border-b border-white/10">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <div className="relative">
                        {/* Vertical line */}
                        <div className="absolute left-6 sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#00FF66] via-[#FFD700] to-[#FF6B35] opacity-30" />

                        <div className="space-y-12">
                            {PHASES.map((phase, idx) => (
                                <div
                                    key={phase.key}
                                    className={`relative ${idx % 2 === 0 ? 'sm:pr-1/2 sm:text-right' : 'sm:pl-1/2'}`}
                                    data-testid={`roadmap-phase-${phase.key}`}
                                >
                                    {/* Dot */}
                                    <div
                                        className="absolute left-6 sm:left-1/2 w-4 h-4 rounded-full -translate-x-1/2 ring-4 ring-[#0A0A0C]"
                                        style={{ backgroundColor: phase.color }}
                                    />
                                    <div className={`pl-14 sm:pl-0 ${idx % 2 === 0 ? 'sm:pr-10' : 'sm:pl-10'}`}>
                                        <Card
                                            className={`bg-white/[0.04] ${phase.highlight ? 'border-[#FFD700]/40 shadow-[0_0_30px_rgba(255,215,0,0.15)]' : 'border-white/10'}`}
                                        >
                                            <CardContent className="p-6">
                                                <div className={`flex items-center gap-3 mb-3 ${idx % 2 === 0 ? 'sm:justify-end' : 'justify-start'}`}>
                                                    <span className="font-mono text-xs text-white/40 uppercase tracking-widest">
                                                        {t(phase.labelKey)}
                                                    </span>
                                                    <StatusBadge status={phase.status} />
                                                </div>
                                                <h3 className="text-xl sm:text-2xl font-black text-white mb-3">
                                                    {t(phase.titleKey)}
                                                </h3>
                                                <ul className={`space-y-2 ${idx % 2 === 0 ? 'sm:items-end' : ''}`}>
                                                    {phase.items.map((itemKey) => (
                                                        <li
                                                            key={itemKey}
                                                            className={`flex items-start gap-2 text-sm text-white/80 ${idx % 2 === 0 ? 'sm:flex-row-reverse sm:text-right' : ''}`}
                                                        >
                                                            <span
                                                                className="w-1.5 h-1.5 rounded-full mt-2 shrink-0"
                                                                style={{ backgroundColor: phase.color }}
                                                            />
                                                            <span>{t(itemKey)}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </CardContent>
                                        </Card>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Special highlight: Big Bang Launch */}
            <section className="py-20 bg-gradient-to-br from-[#FFD700]/5 via-transparent to-[#FF6B35]/5">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                    <Sparkles className="w-12 h-12 text-[#FFD700] mx-auto mb-4" />
                    <div className="font-mono text-xs text-[#FFD700] uppercase tracking-[0.3em] mb-2">
                        {t('roadmap.bigBangPhase')}
                    </div>
                    <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-3 leading-tight">
                        08.08.2026
                    </h2>
                    <p className="text-lg text-white/70 mb-3">{t('roadmap.bigBangDesc')}</p>
                    <p className="text-sm text-[#FFD700] mb-8 font-mono">{t('roadmap.bigBangSubtitle')}</p>

                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/b4p-launch">
                            <Button size="lg" className="bg-[#FFD700] text-[#001A4D] hover:bg-[#FFC700] font-bold px-8 py-6" data-testid="roadmap-cta-launch">
                                <Trophy className="w-5 h-5 mr-2" />
                                {t('roadmap.ctaLaunch')}
                            </Button>
                        </Link>
                        <Link to="/tokenomics">
                            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/5 px-8 py-6" data-testid="roadmap-cta-tokenomics">
                                {t('roadmap.ctaTokenomics')}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
